import { EntityType, useAttachmentsUploadManager } from '@components'

import { Button, ButtonsGroup, Flex, Modal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { invoicingViewTrackingLocationMap } from '@features/invoices/constants/invoicingViewTrackingLocationMap'
import { AttachmentsPicker } from '@features/invoices/elements/AttachmentsPicker'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { useSegment } from '../../../../../../../../hooks'
import { useEInvoicingAddressForm } from '../../../../contexts/eInvoicingAddressFormContext'
import { AddressingLineLookupStatus } from '../../../../enums/addressingLineLookupStatus'
import type { SendAsEInvoiceModalView } from '../../../../types/sendAsEInvoiceModalView'

interface SendAsEInvoiceModalFooterProps {
  attachmentsEnabled: boolean
  onBeforeSubmit?: () => void
  view: SendAsEInvoiceModalView
}

export const SendAsEInvoiceModalFooter = ({
  attachmentsEnabled,
  onBeforeSubmit,
  view,
}: SendAsEInvoiceModalFooterProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const {
    close: closeSendAsEInvoiceModal,
    eInvoicingAddressForm: { trigger, watch: watchEInvoicingAddressForm },
    fetchAddressingLines,
    isLoading,
    isLookupLoading,
    isSubmitting,
    isUnregisteredAddressingLineAcknowledged,
    lookupStatus,
    submitForm,
    validate,
  } = useEInvoicingAddressForm()
  const { isDeleting: isAttachmentsDeleting, isUploading: isAttachmentsUploading } = useAttachmentsUploadManager()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const entityType = watchEInvoicingAddressForm('entityType')
  const isCurrentEntityTypeNotAllowed = isFrenchComplianceMode && entityType === EntityType.Public
  const isFooterActionButtonDisabled =
    isLoading || isSubmitting || isAttachmentsDeleting || isAttachmentsUploading || isCurrentEntityTypeNotAllowed

  const getIsSendingAllowed = useCallback(
    (status?: AddressingLineLookupStatus) => {
      if (status === AddressingLineLookupStatus.Registered) {
        return true
      }

      return status === AddressingLineLookupStatus.Unregistered && isUnregisteredAddressingLineAcknowledged
    },
    [isUnregisteredAddressingLineAcknowledged],
  )

  const isSendingBlockedByLookup =
    isFrenchComplianceMode &&
    (isLookupLoading ||
      (lookupStatus === AddressingLineLookupStatus.Unregistered && !isUnregisteredAddressingLineAcknowledged))

  const isSubmitButtonDisabled = isFooterActionButtonDisabled || isSendingBlockedByLookup

  const areAttachmentsSupported = attachmentsEnabled && !isFrenchComplianceMode

  const handleCancelButtonClick = useCallback(() => {
    closeSendAsEInvoiceModal()
  }, [closeSendAsEInvoiceModal])

  const handleSendButtonClick = useCallback(async () => {
    trigger(isFrenchComplianceMode ? 'addressingLine' : 'eInvoiceAddress.address')
    const isFormValid = await validate()

    if (!isFormValid) {
      return
    }

    const addressingLines = await fetchAddressingLines()

    if (isFrenchComplianceMode && !getIsSendingAllowed(addressingLines)) {
      return
    }

    track(`XXX - Invoicing Core - ${invoicingViewTrackingLocationMap[view]} - Send as e-invoice modal - Send now`)
    onBeforeSubmit?.()
    submitForm()
  }, [
    fetchAddressingLines,
    getIsSendingAllowed,
    isFrenchComplianceMode,
    onBeforeSubmit,
    submitForm,
    track,
    view,
    trigger,
    validate,
  ])

  return (
    <Modal.Footer>
      <Flex justifyContent={areAttachmentsSupported ? 'space-between' : 'flex-end'} alignItems="center" flexGrow={1}>
        {areAttachmentsSupported && <AttachmentsPicker />}
        <ButtonsGroup>
          <Button disabled={isFooterActionButtonDisabled} onClick={handleCancelButtonClick} variant="text">
            {t('external_invoices.send_as_e_invoice.modal.buttons.cancel')}
          </Button>
          <Button
            disabled={isSubmitButtonDisabled}
            loading={isSubmitting || isLookupLoading}
            onClick={handleSendButtonClick}
          >
            {t('external_invoices.send_as_e_invoice.modal.buttons.send_now')}
          </Button>
        </ButtonsGroup>
      </Flex>
    </Modal.Footer>
  )
}
